import { ArrowUpIcon, PlusIcon, XIcon } from '@langchain/untitled-ui-icons';
import { CircularProgress } from '@mui/joy';

import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import FloatingOverflowScrollButton from '@/components/FloatingOverflowScrollButton';
import { Switch } from '@/components/Switch/Switch';
import { Button, IconButton } from '@/design-system';
import GraphIcon from '@/icons/GraphIcon.svg?react';
import { cn } from '@/utils/tailwind';

import { Assistant, useLangGraphClient } from '../src/api';
import { useGraphAssistant } from '../src/api/assistants';
import { useGraphThreadRunsForStatus } from '../src/api/useGraphSwr';
import { checkIsSystemAssistant } from '../src/components/site/Assistants/utils';
import { STUDIO_METADATA, SUPPORTED_FILE_TYPES } from '../src/constants';
import { filterPendingInterurpts } from '../src/control/interrupt';
import { StateContext } from '../src/control/state';
import { useActiveThreadId } from '../src/hooks/useActiveThreadId';
import { StudioModeContext } from '../src/hooks/useStudioMode';
import { useAssistantStore } from '../src/store/assistants/assistantsStore';
import { FilesPreview } from './FilesPreview';
import { InterruptForm } from './InterruptForm';
import { Message } from './Message';
import { useChatContext } from './hooks/useChatContext';
import { useFiles } from './hooks/useFiles';

export const Conversation = ({ messagesKey }: { messagesKey?: string }) => {
  const { setRunState } = useContext(StateContext);

  const { threadId } = useActiveThreadId();
  const thread = useChatContext();
  const { messages, error, joinStream, history } = thread;

  const tasks = history.at(-1)?.tasks ?? [];
  const tasksWithInterrupts = filterPendingInterurpts(tasks);

  const { data: activeRuns } = useGraphThreadRunsForStatus(threadId, 'running');
  const [showingToolCalls, setShowingToolCalls] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { studioMode } = useContext(StudioModeContext);

  const assistantId = useAssistantStore((state) => state.activeAssistantId);
  const { data: assistant } = useGraphAssistant(assistantId);

  const filteredMessages = useMemo(() => {
    return messages.filter((message) => {
      const messageHasToolCalls =
        'tool_calls' in message && !!message.tool_calls?.length;
      if (messageHasToolCalls || message.type === 'tool') {
        return showingToolCalls;
      }
      return true;
    });
  }, [messages, showingToolCalls]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current?.scrollHeight,
      behavior: 'smooth',
    });
  }, [filteredMessages.length, studioMode]);

  const [hasJoinedRun, setHasJoinedRun] = useState(false);
  useEffect(() => {
    if (activeRuns?.length && !hasJoinedRun) {
      joinStream(activeRuns[0].run_id);
      setHasJoinedRun(true);
      setRunState({ type: 'running' });
    }
  }, [activeRuns, hasJoinedRun, joinStream]);

  const getAssistantDisplayName = (a: Assistant | undefined) => {
    if (!a) return;
    if (checkIsSystemAssistant(a)) {
      return `Default (${a.graph_id})`;
    }
    return a.name;
  };

  if (!messages.length) {
    return (
      <div className="mx-auto flex h-full max-w-[800px] flex-col items-center gap-3">
        <div className="flex grow flex-col items-center justify-center gap-2">
          <div className="flex flex-row items-center gap-2 text-primary">
            <GraphIcon className="size-6" />
            <span className="text-xl">LangGraph Studio</span>
          </div>
          <span className="text-xl font-semibold text-disabled">
            {`Start chatting with ${getAssistantDisplayName(assistant)}`}
          </span>
        </div>
        {!!error && <ExpandableErrorAlert error={error} />}
        <InputBar
          showingToolCalls={showingToolCalls}
          setShowingToolCalls={setShowingToolCalls}
          messagesKey={messagesKey}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-full max-w-[1000px] flex-col gap-3 overflow-y-auto">
      <div className="flex flex-col gap-8 overflow-y-auto px-8" ref={scrollRef}>
        {filteredMessages.map((message) => (
          <Message key={message.id} message={message} />
        ))}
        {tasksWithInterrupts.length ? (
          <InterruptForm interrupts={tasksWithInterrupts} />
        ) : null}
      </div>
      <FloatingOverflowScrollButton
        containerRef={scrollRef}
        className="relative w-fit"
      />
      {!!error && <ExpandableErrorAlert error={error} />}
      <InputBar
        showingToolCalls={showingToolCalls}
        setShowingToolCalls={setShowingToolCalls}
      />
    </div>
  );
};

const InputBar = ({
  showingToolCalls,
  setShowingToolCalls,
  messagesKey,
}: {
  showingToolCalls: boolean;
  setShowingToolCalls: (showingToolCalls: boolean) => void;
  messagesKey?: string;
}) => {
  const { setRunState } = useContext(StateContext);
  const [_, apiUrl] = useLangGraphClient();
  const { isLoading, submit, stop, interrupt } = useChatContext();
  const [message, setMessage] = useState('');
  const {
    files,
    setFiles,
    fileInputRef,
    handleFileUpload,
    handleUploadClick,
    handleDeleteFile,
    getFileContentBlocks,
  } = useFiles();

  const constructMessageContent = async () => {
    const fileContentBlocks = await getFileContentBlocks();
    return [{ type: 'text', text: message }, ...fileContentBlocks];
  };

  const onSubmit = async () => {
    if (isLoading) {
      stop();
      setRunState({ type: 'halted' });
      return;
    }
    const messsagesKeyToSubmit = messagesKey ?? 'messages';

    const messageContent = await constructMessageContent();

    submit(
      { [messsagesKeyToSubmit]: [{ type: 'human', content: messageContent }] },
      {
        metadata: STUDIO_METADATA(apiUrl),
        streamSubgraphs: true,
      }
    );
    setRunState({ type: 'running' });
    setMessage('');
    setFiles(new Set());
  };
  const disabled = isLoading || !!interrupt;
  return (
    <div
      className={cn(
        'mt-auto flex w-full flex-col gap-4 rounded-xl border border-secondary bg-primary p-4',
        disabled && 'bg-tertiary'
      )}
    >
      <FilesPreview
        files={files}
        handleDeleteFile={({ file }) => {
          if (!file) return;
          handleDeleteFile(file);
        }}
      />
      <textarea
        placeholder="Enter message"
        className="w-full resize-none rounded-md border-none bg-transparent p-2 text-sm outline-none"
        autoFocus={true}
        value={message}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.metaKey && !e.shiftKey && !disabled) {
            e.preventDefault();
            onSubmit();
          }
        }}
        onChange={(e) => setMessage(e.target.value)}
        disabled={disabled}
      />
      <div className="flex w-full flex-row items-center gap-2">
        <Switch
          checked={showingToolCalls}
          onChange={() => setShowingToolCalls(!showingToolCalls)}
          label="Show tool calls"
          labelClassName="text-xs"
        />
        <Button
          variant="plain"
          color="secondary"
          leftDecorator={PlusIcon}
          onClick={handleUploadClick}
          disabled={disabled}
          className={cn(disabled && 'bg-transparent')}
        >
          Upload files or images
        </Button>
        <div className="ml-auto flex items-center justify-end">
          <SubmitButton
            onSubmit={onSubmit}
            isLoading={isLoading}
            disabled={!message || !!interrupt}
          />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          accept={SUPPORTED_FILE_TYPES.join(',')}
          multiple
        />
      </div>
    </div>
  );
};

const SubmitButton = ({
  onSubmit,
  isLoading,
  disabled,
}: {
  onSubmit: () => void;
  isLoading: boolean;
  disabled: boolean;
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <CircularProgress size="sm" />
        <IconButton
          onClick={onSubmit}
          type="button"
          label="Cancel"
          icon={XIcon}
          size="md"
          round
        />
      </div>
    );
  }
  return (
    <IconButton
      onClick={onSubmit}
      disabled={disabled}
      type="button"
      label="Send message"
      icon={ArrowUpIcon}
      size="md"
      round
    />
  );
};
